import { faArrowsRotate } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import type { ColumnDef, RowData } from "@tanstack/react-table";
import type { ChangeEvent, ReactNode } from "react";
import { DataTableV3UI } from "./DataTableV3UI";
import type { CustomFilterWidgetProps } from "./TableHeader";
import { useDataTableV3 } from "./useDataTableV3";

const DEFAULT_PAGE_SIZES = [5, 10, 20, 50];

declare module "@tanstack/react-table" {
    // eslint-disable-next-line
    interface TableMeta<TData extends RowData> {
        updateData: (
            rowIndex: number,
            itemId: any,
            value: any,
        ) => Promise<void>;
        deleteRow: (rowIndex: number, itemId: any) => Promise<void>;
        refresh: () => Promise<void>;
    }
    // eslint-disable-next-line
    interface ColumnMeta<TData extends RowData, TValue> {
        customFilterWidget?: React.FC<CustomFilterWidgetProps>;
        sortingKey?: string;
        filteringKey?: string;
        // Extra key-value pairs to be passed down to custom filters.
        extraFilterProps?: { [key: string]: any };
    }
}

interface TableFooterProps {
    loadingData: boolean;
    currentPage: number;
    pageCount: number;
    itemCount: number;
    nextPage: () => void;
    nextPageEnabled: boolean;
    previousPage: () => void;
    previousPageEnabled: boolean;
    defaultPageSize: number;
    selectedPageSize: number;
    onPageChange?: (page: number) => void;
    onSelectPageSize: (e: ChangeEvent<HTMLSelectElement>) => void;
}

export const TableFooter = (props: TableFooterProps) => {
    const getPageNumbers = () => {
        const pageNumbers = [];
        const currentPage = props.currentPage;
        const totalPages = props.pageCount;

        if (totalPages <= 6) {
            // Show all pages if we only have up to 6 pages
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            // Always show first page
            pageNumbers.push(1);

            // Adjust ranges based on current page position
            if (currentPage <= 4) {
                pageNumbers.push(...Array.from({ length: 4 }, (_, i) => i + 2));
                pageNumbers.push("...");
                pageNumbers.push(totalPages);
            } else if (currentPage > totalPages - 4) {
                // When close to end, show last 5 pages
                pageNumbers.push("...");
                for (let i = totalPages - 4; i <= totalPages; i++) {
                    pageNumbers.push(i);
                }
            } else {
                // In middle, show current page and neighbors
                pageNumbers.push("...");
                for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pageNumbers.push(i);
                }
                pageNumbers.push("...");
                pageNumbers.push(totalPages);
            }
        }

        return pageNumbers;
    };

    return (
        <div className="rounded-b bg-white border-slate-200 flex items-center justify-between w-full text-sm font-medium p-3 text-ae-blue-900">
            <div>{props.itemCount} Items</div>

            {props.loadingData && (
                <div className="flex items-center gap-2">
                    Fetching data...
                    <FontAwesomeIcon
                        icon={faArrowsRotate}
                        className="w-4 animate-spin"
                    />
                </div>
            )}

            <div className="flex items-center">
                <button
                    type="button"
                    className={`mr-2 p-1 rounded-md ${
                        props.previousPageEnabled
                            ? "border border-transparent hover:border-ae-blue-550 hover:text-ae-blue-550"
                            : "text-neutral-400"
                    }`}
                    onClick={props.previousPage}
                    disabled={!props.previousPageEnabled}
                >
                    <ChevronLeftIcon className="h-5" />
                </button>

                <div className="flex gap-1">
                    {getPageNumbers().map((pageNum) =>
                        pageNum === "..." ? (
                            <button
                                type="button"
                                key={`ellipsis`}
                                className="px-2 py-1 rounded-md border border-transparent hover:border-ae-blue-550 hover:text-ae-blue-550"
                                onClick={() => {
                                    const page = prompt("Enter page number:");
                                    const pageNumber = parseInt(page || "", 10);
                                    if (
                                        pageNumber &&
                                        pageNumber > 0 &&
                                        pageNumber <= props.pageCount
                                    ) {
                                        props.onPageChange?.(pageNumber);
                                    }
                                }}
                            >
                                ...
                            </button>
                        ) : (
                            <button
                                type="button"
                                key={pageNum}
                                className={`px-3 py-1 rounded-md border ${
                                    pageNum === props.currentPage
                                        ? "border-ae-blue-550 text-ae-blue-550"
                                        : "border-transparent hover:border-ae-blue-550 hover:text-ae-blue-550"
                                }`}
                                onClick={() => props.onPageChange?.(pageNum)}
                            >
                                {pageNum}
                            </button>
                        ),
                    )}
                </div>
                <button
                    type="button"
                    className={`p-1 rounded-md ${
                        props.nextPageEnabled
                            ? "border border-transparent hover:border-ae-blue-550 hover:text-ae-blue-550"
                            : "text-neutral-400"
                    }`}
                    onClick={props.nextPage}
                    disabled={!props.nextPageEnabled}
                >
                    <ChevronRightIcon className="h-5" />
                </button>
                <select
                    value={props.selectedPageSize}
                    onChange={props.onSelectPageSize}
                    className="ml-4 h-7 text-sm rounded border-neutral-300 py-1"
                >
                    {DEFAULT_PAGE_SIZES.map((pageSize) => (
                        <option key={pageSize} value={pageSize}>
                            {pageSize}
                        </option>
                    ))}
                    {props.defaultPageSize &&
                        !DEFAULT_PAGE_SIZES.includes(props.defaultPageSize) && (
                            <option
                                key={props.defaultPageSize}
                                value={props.defaultPageSize}
                            >
                                {props.defaultPageSize}
                            </option>
                        )}
                </select>
            </div>
        </div>
    );
};

interface RowActionButtonsProps {
    actions: {
        fn: () => void;
        icon: ReactNode;
        tooltip?: string;
    }[];
}

export const RowActionButtons = (props: RowActionButtonsProps) => {
    return (
        <div className="w-full flex items-center justify-end gap-1 -my-2">
            {props.actions.map((action, index) => {
                return (
                    <div
                        key={index}
                        className="group relative flex items-center"
                    >
                        <button
                            type="button"
                            onClick={action.fn}
                            className="h-9 w-9 flex items-center justify-center text-ae-blue-900 p-1 rounded hover:border-2 border-ae-gray-300"
                        >
                            {action.icon}
                        </button>
                        {action.tooltip && (
                            <span className="z-0 absolute right-full px-3 py-1 mr-3 text-sm bg-white border border-white rounded drop-shadow-md opacity-0 group-hover:opacity-100 invisible group-hover:visible whitespace-nowrap">
                                {action.tooltip}
                                <svg
                                    className="absolute top-1/2 -right-2 h-2 w-2 fill-current text-white transform -translate-y-1/2"
                                    viewBox="0 0 20 20"
                                >
                                    <title>{action.tooltip}</title>
                                    <polygon points="0,0 20,10 0,20" />
                                </svg>
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export interface DataTableV3Props<T> {
    // TODO: improve typing
    dataName: string;
    columns: ColumnDef<T, any>[];
    fetchFunction: any;
    sortable: boolean;
    filterable: boolean;
    extraFilters: {
        [key: string]: any;
    };
    onClickRow?: (string) => void;
    defaultPageSize?: number;
    partialUpdate?: (id: string, value: Partial<T>) => Promise<void>;
    deleteRow?: (id: string) => Promise<void>;
    createRow?: () => void;
    getRowId?: (row: T) => any;
    selectable?: boolean;
}

/**
 * New data table, using atoms for state management.
 * This component now uses the useDataTableV3 hook for logic and DataTableV3UI for rendering.
 */
export const DataTableV3 = <T,>(props: DataTableV3Props<T>) => {
    const { table, dataQuery, dataName } = useDataTableV3({
        dataName: props.dataName,
        columns: props.columns,
        fetchFunction: props.fetchFunction,
        sortable: props.sortable,
        filterable: props.filterable,
        selectable: props.selectable,
        extraFilters: props.extraFilters,
        defaultPageSize: props.defaultPageSize,
        partialUpdate: props.partialUpdate,
        deleteRow: props.deleteRow,
        getRowId: props.getRowId,
    });

    return (
        <DataTableV3UI
            table={table}
            dataName={dataName}
            columns={props.columns}
            loadingData={dataQuery.isFetching}
            itemCount={dataQuery.data?.count || 0}
            onClickRow={props.onClickRow}
            createRow={props.createRow}
            defaultPageSize={props.defaultPageSize}
        />
    );
};
